import { Audience } from '@/models/audiences';
import { DataSource, DataSourceIDEnum, type DataSourceCodeConfig } from '@/models/dataSource';
import { FISCAL_CODE_TRAIT_ID } from './constants';

const UPLOAD_TYPES: string[] = [DataSourceIDEnum.CsvFile, DataSourceIDEnum.EncryptedCsvFile];

/**
 * True when the expression is only the base "fiscal code is set" rule, i.e. the audience
 * selects its whole datasource with no extra filter. Combined lists (saved from the
 * composer) always carry at least one additional group.
 */
export const isBareFiscalExpression = (expression: Audience['expression']): boolean => {
  const groups = Object.values(expression ?? {});
  if (groups.length !== 1) return false;
  const conditions = Object.values(groups[0] ?? {});
  if (conditions.length !== 1) return false;
  const condition = conditions[0];
  return condition?.property === FISCAL_CODE_TRAIT_ID && condition?.comparisonOp === 'NOT_NULL';
};

/**
 * A Comuni "lista caricata" is the full content of one uploaded file: exactly one CSV
 * datasource and no filter beyond the fiscal-code rule. Everything else (several
 * datasources and/or filters) is a combined list.
 *
 * Classified on the expression rather than on `audience.name === datasource.name`: names
 * drift (a different case or a rename from Fonte dati is enough) and a demoted list stops
 * being selectable as recipients.
 */
export const isUploadedList = (audience: Audience, datasources: DataSource[]): boolean =>
  datasources.length === 1 && UPLOAD_TYPES.includes(datasources[0]?.type?.id) && isBareFiscalExpression(audience.expression);

export const isCitizenFormList = (audience: Audience, datasources: DataSource[]): boolean =>
  datasources.length === 1 &&
  datasources[0]?.type?.id === DataSourceIDEnum.WebsiteJS &&
  !!(datasources[0].configuration as DataSourceCodeConfig | undefined)?.citizenForm &&
  isBareFiscalExpression(audience.expression);
